import type { Metadata } from "@/lib/head";
import { Link } from "@/lib/nav";

import CustomerScreenShell from "@/components/customer-screen-shell";
import Pill, { type PillTone } from "@buildpy/ui/pill";
import SectionCard from "@buildpy/ui/section-card";
import { EMPTY_MARK, formatDate } from "@buildpy/core/lib/format";
import ChangeOrderStatusBadge from "@/modules/change-order/ui/status-badge";
import Money from "@/components/money";
import { DEMO_CHANGE_ORDERS, DEMO_MESSAGES, DEMO_ORG, DEMO_PROJECT } from "@buildpy/core/modules/demo/data";
import { DEMO_PATH } from "@buildpy/core/modules/demo/routes";
import { messageItemOf } from "@buildpy/core/modules/message/rules";
import DemoMessageCompose from "@/modules/demo/ui/demo-message-compose";
import DemoNoSaveButton from "@/modules/demo/ui/demo-no-save-button";
import DemoPhoto from "@/modules/demo/ui/demo-photo";
import MessageList from "@/modules/message/ui/message-list";
import { toCustomerChangeOrders, type CustomerPhoto } from "@buildpy/core/modules/project/customer-visible";
import { PROJECT_LOG_KIND_LABEL, PROJECT_PHASE_STATUS_LABEL, type ProjectPhaseStatus } from "@buildpy/core/modules/project/types";
import { LOG_KIND_TONE } from "@/modules/project/ui/log-kind-tone";
import ProjectStatusBadge from "@/modules/project/ui/status-badge";
import CustomerHeadOrg from "@/modules/share/ui/customer-head-org";

export const metadata: Metadata = {
  title: "내 현장 데모",
  robots: { index: false, follow: true },
};

// 고객 「내 현장」(/c/[token]/project) 과 같은 톤.
const PHASE_TONE: Record<ProjectPhaseStatus, PillTone> = {
  PENDING: "neutral",
  ACTIVE: "progress",
  DONE: "done",
  HOLD: "hold",
};

const LINK_CLASS = "tap-target relative text-sm text-muted-foreground underline-offset-2 hover:underline";

const PhotoGrid = ({ photos, testId }: { photos: readonly CustomerPhoto[]; testId: string }) => (
  <ul className="grid grid-cols-3 gap-1" data-testid={testId}>
    {photos.map((p, i) => (
      <li key={p.id}>
        <DemoPhoto caption={p.caption || p.phase_name || `현장 사진 ${i + 1}`} index={i} />
      </li>
    ))}
  </ul>
);

const period = (from: string | null, to: string | null): string =>
  from || to ? `${formatDate(from)} ~ ${formatDate(to)}` : EMPTY_MARK;

// 고객 「내 현장」 데모 — 실제 화면과 같은 구성품으로 같은 순서를 그리고, 저장이 붙은 자리는 토스트로 바꾼다.
const Page = () => {
  const view = DEMO_PROJECT;
  const changeOrders = toCustomerChangeOrders(DEMO_CHANGE_ORDERS.map((d) => d.change_order));

  return (
    <CustomerScreenShell className="p-0 pb-10">
      <header className="sticky top-(--safe-top) z-10 flex items-center justify-between gap-2 border-b border-border bg-card px-4 py-2.5">
        <CustomerHeadOrg name={DEMO_ORG.name} tel={DEMO_ORG.tel} />
        <div className="flex shrink-0 items-center gap-x-3">
          <Link className={LINK_CLASS} href={DEMO_PATH.statement}>
            현재 내역서
          </Link>
          <Link className={LINK_CLASS} href={DEMO_PATH.quote}>
            견적서 보기
          </Link>
        </div>
      </header>

      <div className="mx-auto flex w-full max-w-md flex-col gap-y-4 px-4 pt-4">
        <SectionCard className="text-sm" data-testid="customer-project-summary">
          <div className="flex items-start justify-between gap-2">
            <h1 className="text-base font-semibold">{view.title}</h1>
            <ProjectStatusBadge status={view.status} />
          </div>
          <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
            <dt className="text-muted-foreground">착공일</dt>
            <dd className="tabular-nums">{formatDate(view.started_at)}</dd>
            <dt className="text-muted-foreground">준공 예정</dt>
            <dd className="tabular-nums">{formatDate(view.planned_end_date)}</dd>
            <dt className="text-muted-foreground">담당</dt>
            <dd>{view.assignee_name ?? EMPTY_MARK}</dd>
          </dl>
        </SectionCard>

        <section className="flex flex-col gap-y-2" data-testid="customer-project-phases">
          <h2 className="text-base font-semibold">공정</h2>
          <ol className="flex flex-col gap-y-1.5">
            {view.phases.map((p, i) => (
              <SectionCard
                key={`${i}-${p.name}`}
                as="li"
                className="flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm"
                padding="none">
                <div className="min-w-0">
                  <p className="truncate font-medium">{p.name}</p>
                  <p className="text-xs tabular-nums text-muted-foreground">{period(p.start_date, p.end_date)}</p>
                </div>
                <Pill className="shrink-0" tone={PHASE_TONE[p.status]}>
                  {PROJECT_PHASE_STATUS_LABEL[p.status]}
                </Pill>
              </SectionCard>
            ))}
          </ol>
        </section>

        <section className="flex scroll-mt-16 flex-col gap-y-2" data-testid="customer-project-change-orders" id="change-orders">
          <h2 className="text-base font-semibold">변경공사</h2>
          <ul className="flex flex-col gap-y-1.5">
            {changeOrders.map((co) => (
              <SectionCard
                key={co.seq}
                as="li"
                className="flex flex-wrap items-center gap-2 rounded-md px-3 py-2 text-sm"
                padding="none">
                <ChangeOrderStatusBadge status={co.status} />
                <span className="sr-only">, </span>
                <span className="font-medium">변경 {co.seq}차</span>
                <span className="sr-only">, 증감 </span>
                <Money className="text-muted-foreground" signed value={co.delta_total} />
                {co.status === "SENT" && (
                  <DemoNoSaveButton className="ml-auto" size="sm" variant="outline">
                    승인하기
                  </DemoNoSaveButton>
                )}
              </SectionCard>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground">승인한 변경공사는 현재 내역서에 바로 반영됩니다.</p>
        </section>

        <section className="flex flex-col gap-y-2" data-testid="customer-project-messages" id="messages">
          <h2 className="text-base font-semibold">담당자와 대화</h2>
          <MessageList
            emptyText="궁금한 점을 남겨 주시면 담당자가 답해 드립니다."
            messages={DEMO_MESSAGES.map(messageItemOf)}
            testId="customer-message-list"
            viewer="CUSTOMER"
          />
          <DemoMessageCompose />
        </section>

        <section className="flex flex-col gap-y-2" data-testid="customer-project-days">
          <h2 className="text-base font-semibold">현장 소식</h2>
          <ol className="flex flex-col gap-y-3">
            {view.days.map((day) => (
              <SectionCard key={day.date} as="li" className="flex flex-col gap-y-2 text-sm" padding="sm">
                <p className="font-semibold tabular-nums">{formatDate(day.date)}</p>
                {day.logs.map((log) => (
                  <div key={log.id} className="flex flex-col gap-y-1">
                    <Pill className="self-start" tone={LOG_KIND_TONE[log.kind]}>
                      {PROJECT_LOG_KIND_LABEL[log.kind]}
                    </Pill>
                    <p className="whitespace-pre-wrap break-words">{log.body}</p>
                  </div>
                ))}
                {day.photos.length > 0 && <PhotoGrid photos={day.photos} testId="customer-day-photos" />}
              </SectionCard>
            ))}
          </ol>
        </section>

        <section className="flex flex-col gap-y-2" data-testid="customer-project-gallery">
          <h2 className="text-base font-semibold">사진 모아보기</h2>
          <PhotoGrid photos={view.photos} testId="customer-gallery-photos" />
        </section>
      </div>
    </CustomerScreenShell>
  );
};

export default Page;
